import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent } from '@healthcatalyst/cashmere';
import { StatusService } from '../../shared/services/status.service';
import { SourceService } from '../../shared/services/source.service';
import { TypeService } from '../../shared/services/type.service';
import { AdminStringEditorComponent } from '../string-editor/admin-string-editor.component';

/** Admin Sources */
@Component({
    selector: 'pd-admin-sources',
    templateUrl: './admin-sources.component.html',
    styleUrls: ['./admin-sources.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminSourcesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    sourceList: any[] = [];
    loading = true;
    loadError = false;
    adminSourcesDisplayedColumns: string[] = [
        'item',
        'name',
        'type',
        'status',
        'revisionDate'
    ];
    adminSourcesPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    statusFilters: string[] = [];
    typeFilters: string[] = [];
    statusText = 'View All';
    typeText = 'View All';
    statusKey: { [key: string]: string } = {};
    typeKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminSourcesListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private sourceService: SourceService,
        private statusService: StatusService,
        private typeService: TypeService,
        private modalService: ModalService
    ) { }

    ngOnInit(): void {
        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if ( this.typeKey ) {
                    this.loadSources();
                }
            } else {
                this.statusService.refresh();
            }
        });

        this.typeService.catTypeKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list && this.typeService.category === 'source') {
                this.typeKey = list;
                if ( this.statusKey ) {
                    this.loadSources();
                }
            } else {
                this.typeService.loadCatTypes('source');
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.initSorting();
    }

    loadSources(): void {
        this.sourceService.sourceListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.sourceList = [];

                // Parse the id fields for table content
                list.forEach(element => {
                    let tempItem = {
                        'item': element.item,
                        'name': element.name,
                        'status': '',
                        'type': '',
                        'revisionDate': element.revisionDate,
                        'sourceID': element.sourceID
                    };

                    tempItem['status'] = this.formatID( element['statusID'], 'statusID');
                    tempItem['type'] = this.formatID( element['typeID'], 'typeID');

                    this.sourceList.push( tempItem );
                });

                this.dataSource = new HcTableDataSource(this.sourceList);
                
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.initSorting();
                }
            } else {
                this.loadError = this.sourceService.loadFailed;
                if (!this.loadError) {
                    this.sourceService.refresh();
                } else {
                    this.loading = false;
                }
            }
        })
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'revisionDate': 
                    const sortDate = new Date(item.revisionDate);
                    return sortDate.getTime();
                case 'item':
                    if ( item.item.startsWith('AR') ) {
                        const sortStr = item.item.replace('AR', '');
                        const sortNum = Number(sortStr) + 9999999999999;
                        return sortNum;
                    } else {
                        return Number( item.item );
                    }
                case 'type':
                case 'status':
                case 'name': {
                    return item[property];
                }
                default: {
                    return '';
                }
            }
        };
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            case 'type':
                if (filterArray.length == 1) this.typeText = filterArray[0];
                else if (filterArray.length > 1) this.typeText = `${filterArray.length} selected`;
                else this.typeText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        for (let i = 0; i < this.sourceList.length; i++) {
            if (this.sourceList[i][type]) {
                if (!filterData.includes(this.sourceList[i][type])) {
                    filterData.push(this.sourceList[i][type]);
                }
            }
        }
        this.filterData = filterData;
    }

    filterSources() {
        let filteredSources = [];

        for (let i = 0; i < this.sourceList.length; i++) {
            let typeFilterMatch = this.typeFilters.length === 0;
            if (this.typeFilters.includes(this.sourceList[i]['type'])) {
                typeFilterMatch = true;
            }

            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.statusFilters.includes(this.sourceList[i]['status'])) {
                statusFilterMatch = true;
            }

            if (typeFilterMatch && statusFilterMatch) {
                filteredSources.push(this.sourceList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered technology list
        this.dataSource = new HcTableDataSource(filteredSources);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);
        
        if (this.filterType === 'type') {
            this.typeFilters = filterArray;
        } else if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        }

        this.filterSources();
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    openStringEditor(type: string) {
        const options: ModalOptions = {
            closeIcon: true,
            size: 'md',
            data: {
                type: type,
                category: 'Source'
            }
        };

        this.modalService.open(AdminStringEditorComponent, options);
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === "typeID") {
            reformattedID = this.typeKey[id.toString()];
        } else if (type === 'statusID') {
            reformattedID = this.statusKey[id.toString()];
        }

        return reformattedID;
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
